import { PublishStatus } from '@src/types.ts/publishRecord';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { isOutdated } from '@src/utility/publishFingerprints';

import type {
  PublishFingerprints,
  PublishRecord,
} from '@src/types.ts/publishRecord';

export interface AlreadyPublishedSummary {
  /** How many of the selected languages already have a Succeeded render. */
  publishedCount: number;
  /** Of those, how many have a stale fingerprint (re-publishing would update). */
  outdatedCount: number;
  /**
   * Formatted caption of the matched language (Original suffixed). Last-write-
   * wins across the selection, so it is only meaningful when a single language
   * is selected — the caller uses it solely in the one-language message.
   */
  singleLabel: string;
}

/**
 * Summarize whether the currently selected languages have already been
 * published for a clip (the "already published" advisory in the Publish &
 * Share panel). A language "already exists" when there is a Succeeded
 * `PublishRecord` for it; resolution is deliberately ignored and the aspect
 * ratio is implicitly scoped by whichever `records` are passed in.
 *
 * `currentFingerprintFor` reads MobX observables, so like the Downloads tab
 * this must be called in an `observer`'s render path (not memoized) to stay
 * reactive.
 */
export function summarizeAlreadyPublished(
  records: PublishRecord[],
  selectedLanguages: Iterable<string>,
  originalLang: string,
  currentFingerprintFor: (language: string) => PublishFingerprints | null,
): AlreadyPublishedSummary {
  const succeededRecords = records.filter(
    r => r.publishStatus === PublishStatus.Succeeded,
  );
  const matchesLanguage = (
    recordLang: string,
    selectedLang: string,
  ): boolean =>
    isOriginalLang(selectedLang)
      ? isOriginalLang(recordLang) || recordLang === originalLang
      : recordLang === selectedLang;

  let publishedCount = 0;
  let outdatedCount = 0;
  let singleLabel = '';
  for (const lang of selectedLanguages) {
    const match = succeededRecords.find(r => matchesLanguage(r.language, lang));
    if (!match) continue;
    publishedCount += 1;
    singleLabel = isOriginalLang(lang)
      ? `${formatLanguageCaption(originalLang)} (Original)`
      : formatLanguageCaption(lang);
    const current = currentFingerprintFor(lang);
    if (current && isOutdated(match, current)) outdatedCount += 1;
  }

  return { publishedCount, outdatedCount, singleLabel };
}
